import React, { useState } from 'react'
import ErrorMessage from '../ErrorMessage/ErrorMessage'
import { retrieveAddress } from '../../services/api/orders'
import { UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormTrigger } from 'react-hook-form'

interface Invoice3rdPartyFormProps {
    register: UseFormRegister<any>
    formState: any
    autoComplete?: boolean
    getValues: UseFormGetValues<any>
    setValue: UseFormSetValue<any>
    trigger: UseFormTrigger<any>
    disableEmail?: boolean
}

const Invoice3rdPartyForm: React.FunctionComponent<Invoice3rdPartyFormProps> = ({
    register,
    formState,
    autoComplete = true,
    getValues,
    setValue,
    trigger,
    disableEmail = false
}) => {
    const [loadingAddress, setLoadingAddress] = useState<boolean>(false)
    const tryAdressAutocomplete = async () => {
        const { zipcode, houseNumber, extension, city, street } = getValues()

        trigger(['zipcode', 'houseNumber'])

        if (!zipcode || !houseNumber) {
            return
        }
        setLoadingAddress(true)
        try {
            // get autocomplete results
            const res = await retrieveAddress({ zipcode, houseNumber, extension })

            // Only overwrite values if not already filled
            if (res.city && !city) {
                setValue('city', res.city)
            }
            trigger('city')
            if (res.street && !street) {
                setValue('street', res.street)
            }
            trigger('street')
            if (res.extension && !extension) {
                setValue('extension', res.extension)
            }
            trigger('extension')
        } catch (e) {
            console.log(e)
        }
        setLoadingAddress(false)
    }

    return (
        <form className='flex flex-col space-y-4'>
            <div className='flex flex-col space-y-1'>
                <label htmlFor='name' className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>
                    Naam op factuur
                </label>
                <input
                    {...register('name', { required: 'Naam is verplicht' })}
                    placeholder='Naam op factuur'
                    autoComplete='billing family-name'
                    id='name'
                    type={'text'}
                    className='w-full'
                />
                <ErrorMessage
                    message={
                        typeof formState.errors.name?.message === 'string' ? formState.errors.name?.message : undefined
                    }
                />
            </div>

            <div className='flex flex-col space-y-1'>
                <label htmlFor='emailAddress' className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>
                    E-mailadres klant
                </label>
                <input
                    {...register('emailAddress', {
                        required: 'E-mailadres is verplicht',
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Ongeldig e-mailadres'
                        },
                        disabled: disableEmail
                    })}
                    id='emailAddress'
                    placeholder='E-mailadres van de klant'
                    type={'email'}
                    className='w-full'
                />
                <ErrorMessage
                    message={
                        typeof formState.errors.emailAddress?.message === 'string'
                            ? formState.errors.emailAddress?.message
                            : undefined
                    }
                />
            </div>

            <div className='flex flex-wrap md:flex-nowrap space-y-4 md:space-y-0 md:space-x-4 mb-1'>
                <div className='w-full flex-grow'>
                    <label htmlFor='zipcode' className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>
                        Postcode
                    </label>
                    <input
                        {...register('zipcode', { required: 'Postcode is verplicht' })}
                        placeholder='Postcode'
                        type={'text'}
                        id='zipcode'
                        className='w-full'
                        onBlur={() => autoComplete && tryAdressAutocomplete()}
                        disabled={loadingAddress}
                    />
                    <ErrorMessage
                        message={
                            typeof formState.errors.zipcode?.message === 'string'
                                ? formState.errors.zipcode?.message
                                : undefined
                        }
                    />
                </div>
                <div className='flex-grow w-full'>
                    <label htmlFor='houseNumber' className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>
                        Huisnummer
                    </label>
                    <input
                        {...register('houseNumber', {
                            required: 'Huisnummer is verplicht',
                            valueAsNumber: true
                        })}
                        placeholder='Huisnummer'
                        type={'number'}
                        id='houseNumber'
                        className='w-full rounded focus:outline-none px-3 py-1.5'
                        min={1}
                        onBlur={() => autoComplete && tryAdressAutocomplete()}
                        disabled={loadingAddress}
                    />
                    <ErrorMessage
                        message={
                            typeof formState.errors.houseNumber?.message === 'string'
                                ? formState.errors.houseNumber?.message
                                : undefined
                        }
                    />
                </div>
                <div className='flex-shrink'>
                    <label htmlFor='extension' className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>
                        Extensie
                    </label>
                    <input
                        {...register('extension')}
                        placeholder='A'
                        type={'text'}
                        id='extension'
                        className='w-full'
                        onBlur={() => autoComplete && tryAdressAutocomplete()}
                        disabled={loadingAddress}
                    />
                </div>
            </div>

            <div className='flex flex-col space-y-1'>
                <label htmlFor='street' className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>
                    Straat
                </label>
                <input
                    {...register('street', { required: 'Straat is verplicht' })}
                    disabled={loadingAddress}
                    id='street'
                    placeholder='Voorbeeldlaan'
                    type={'text'}
                    className='mb-1'
                />
                <ErrorMessage
                    message={
                        typeof formState.errors.street?.message === 'string'
                            ? formState.errors.street?.message
                            : undefined
                    }
                />
            </div>

            <div className='flex flex-col space-y-1'>
                <label htmlFor='city' className='text-sm font-medium text-[--tb-mainHeading] mb-1 block'>
                    Plaats
                </label>
                <input
                    {...register('city', { required: 'Plaats is verplicht' })}
                    disabled={loadingAddress}
                    id='city'
                    placeholder='Voorbeeldplaats'
                    type={'text'}
                    className='mb-1'
                />
                <ErrorMessage
                    message={
                        typeof formState.errors.city?.message === 'string' ? formState.errors.city?.message : undefined
                    }
                />
            </div>
        </form>
    )
}
export default Invoice3rdPartyForm
